/** PNG / SVG export (whole board, selection, a frame) and board thumbnails. */

import { exportToBlob, exportToSvg, hashElementsVersion } from '@excalidraw/excalidraw';
import type { ExcalidrawElement, ExcalidrawFrameLikeElement, NonDeletedExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import type { AppState, BinaryFiles, ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';

export type ExportScope = { kind: 'board' } | { kind: 'selection' } | { kind: 'frame'; frameId: string };
export type ExportFormat = 'png' | 'svg';

export interface Snapshot {
  elements: readonly NonDeletedExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
}

export function snapshot(xapi: ExcalidrawImperativeAPI): Snapshot {
  return { elements: xapi.getSceneElements(), appState: xapi.getAppState(), files: xapi.getFiles() };
}

export function frames(elements: readonly ExcalidrawElement[]): ExcalidrawFrameLikeElement[] {
  return elements.filter((e): e is ExcalidrawFrameLikeElement => !e.isDeleted && (e.type === 'frame' || e.type === 'magicframe'));
}

/** Elements for a scope (selection pulls in bound labels and frame children). */
export function scopeElements(s: Snapshot, scope: ExportScope): { elements: NonDeletedExcalidrawElement[]; frame: ExcalidrawFrameLikeElement | null } {
  const all = s.elements;
  if (scope.kind === 'board') return { elements: [...all], frame: null };
  if (scope.kind === 'frame') {
    const frame = frames(all).find((f) => f.id === scope.frameId) ?? null;
    if (!frame) return { elements: [], frame: null };
    return { elements: all.filter((e) => e.id === frame.id || e.frameId === frame.id), frame };
  }
  const sel = new Set(Object.keys(s.appState.selectedElementIds).filter((k) => s.appState.selectedElementIds[k]));
  const selFrames = new Set(all.filter((e) => sel.has(e.id) && e.type === 'frame').map((e) => e.id));
  const out = all.filter(
    (e) =>
      sel.has(e.id) ||
      (e.type === 'text' && e.containerId != null && sel.has(e.containerId)) ||
      (e.frameId != null && selFrames.has(e.frameId)),
  );
  return { elements: out, frame: null };
}

function exportAppState(s: Snapshot): Partial<AppState> {
  return {
    ...s.appState,
    exportBackground: true,
    exportWithDarkMode: false,
    viewBackgroundColor: s.appState.viewBackgroundColor || '#ffffff',
  };
}

export async function exportScope(s: Snapshot, scope: ExportScope, format: ExportFormat): Promise<Blob | null> {
  const { elements, frame } = scopeElements(s, scope);
  if (!elements.length) return null;
  const common = { elements, appState: { ...exportAppState(s), exportScale: 2 }, files: s.files, exportPadding: frame ? 0 : 24, exportingFrame: frame };
  if (format === 'png') return exportToBlob({ ...common, mimeType: 'image/png' });
  const svg = await exportToSvg(common);
  return new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' });
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function safeFilename(s: string): string {
  return (s.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-') || 'board').slice(0, 80);
}

/** Version hash of the visible scene (thumbnail "changed since last upload" check). */
export function sceneHash(s: Snapshot): number {
  return hashElementsVersion(s.elements);
}

export async function thumbnailBlob(s: Snapshot): Promise<Blob | null> {
  if (!s.elements.length) return null;
  return exportToBlob({
    elements: s.elements,
    appState: { ...exportAppState(s), exportScale: 1 },
    files: s.files,
    mimeType: 'image/png',
    maxWidthOrHeight: 480,
    exportPadding: 32,
  });
}
